import { useLocale, useTranslations } from 'next-intl';
import { useLanguages } from 'providers/ClientProviders/useLanguages';
import { useCallback, useMemo } from 'react';
import { Option } from 'types/Option';
import { availableLanguageOptions } from 'utils/availableLanguageOptions';
import { getMostUsedTargetLanguagesUtil } from 'utils/getMostUsedTargetLanguagesUtil';

interface IUseOptionsPairLanguagesIn {
  noTargeLanguage?: boolean;
  excluded?: string[];
  search?: string;
  noSpecification?: boolean;
}
interface IUseOptionsPairLanguagesOut {
  optionsOrderableTargetLanguages: Option[];
  isLoading: boolean;
}

export default function useOptionsOrderableTargetLanguages({
  noTargeLanguage = false,
  excluded = [],
  search = '',
  noSpecification = false
}: IUseOptionsPairLanguagesIn = {}): IUseOptionsPairLanguagesOut {
  const t = useTranslations();
  const locale = useLocale();

  const { data, isLoading } = useLanguages();

  const optionsOrderableTargetLanguagesInital = useMemo(() => {
    return noTargeLanguage
      ? []
      : [
          {
            value: '',
            label: t('ordering_form.target_language'),
            disabled: true,
            hidden: true
          }
        ];
  }, [noTargeLanguage, t]);

  const mostUsedLanguage = getMostUsedTargetLanguagesUtil(
    data?.languagePairs?.map((item) => item.targetLanguageCode as string) ?? []
  );

  const factoryTargetLanguages = useCallback(() => {
    const options = [
      ...optionsOrderableTargetLanguagesInital,
      ...availableLanguageOptions(
        data?.allOrderableTargetLanguages?.map((item) => item.code as string) ?? [],
        excluded,
        mostUsedLanguage,
        '',
        t,
        locale
      )
    ].filter((item) => item.label.toLocaleLowerCase().includes(search.toLocaleLowerCase()));

    if (noSpecification) {
      const map = new Map<string, Option>();

      options.forEach((option) => {
        const value = option.value as string;

        if (value.includes('_')) {
          const code = value.split('_')[0];

          map.set(code, { label: t(`languages.${code}` as any), value: code });
        } else map.set(value, option);
      });

      return [...map.values()];
    }

    return options;
  }, [
    data?.allOrderableTargetLanguages,
    excluded,
    locale,
    mostUsedLanguage,
    noSpecification,
    optionsOrderableTargetLanguagesInital,
    search,
    t
  ]);

  const optionsOrderableTargetLanguages = factoryTargetLanguages();

  return { optionsOrderableTargetLanguages, isLoading };
}
